import { Button as MuiButton, ButtonProps as MuiButtonProps } from '@mui/material'
import { forwardRef } from 'react'

/**
 * Custom Button variants
 */
export type ButtonVariant = 'primary' | 'secondary' | 'outline'

/**
 * Button component props
 */
export interface ButtonProps extends Omit<MuiButtonProps, 'variant' | 'color'> {
  /** Button variant style */
  variant?: ButtonVariant
  /** Button size */
  size?: 'small' | 'medium' | 'large'
  /** Make button full width */
  fullWidth?: boolean
  /** Click handler */
  onClick?: (event: React.MouseEvent<HTMLButtonElement>) => void
  /** Link href (renders as link button) */
  href?: string
  /** Children content */
  children: React.ReactNode
}

/**
 * Custom Button component with primary, secondary, and outline variants
 * Extends Material UI Button with consistent styling
 */
export const Button = forwardRef<HTMLButtonElement, ButtonProps>(
  ({ variant = 'primary', size = 'medium', children, ...props }, ref) => {
    // Map custom variants to MUI variants and colors
    const getMuiVariant = (): MuiButtonProps['variant'] => {
      if (variant === 'outline') return 'outlined'
      return 'contained'
    }

    const getMuiColor = (): MuiButtonProps['color'] => {
      if (variant === 'secondary') return 'secondary'
      return 'primary'
    }

    return (
      <MuiButton
        ref={ref}
        variant={getMuiVariant()}
        color={getMuiColor()}
        size={size}
        {...props}
      >
        {children}
      </MuiButton>
    )
  }
)

Button.displayName = 'Button'
